import { oc } from "@orpc/contract";
import { z } from "zod";
import { Cents } from "./money";

/**
 * Refunds (CONTEXT.md `refund`, #103) and their credit invoices (`creditInvoice`, #104), staff
 * only. A refund gives back a quantity of order lines and optionally the shipping line through
 * Pay.nl, never more than was paid per line. One that went through gets a credit invoice in its own
 * series (`CBC-100000…`) and, with "Voorraad terugboeken" (on by default), its stock back at the
 * default location (a composed bundle's on its components).
 *
 * A refund is `pending` while Pay.nl is asked, then `succeeded` or `failed`. When Pay.nl's answer
 * is lost it stays `pending`: an employee checks Pay.nl and resolves it (`resolve`).
 */

export const RefundStatus = z.enum(["pending", "succeeded", "failed"]);
export type RefundStatus = z.infer<typeof RefundStatus>;

export const RefundAmountsPerRate = z.object({
	vatRate: z.number().int(),
	excl: Cents,
	vat: Cents,
	incl: Cents,
});

/** A credit invoice, as the order detail lists it. Amounts negative, as printed. */
export const CreditInvoiceSummary = z.object({
	id: z.string(),
	/** `CBC-100000`. */
	number: z.string(),
	/** The invoice it corrects, `CBI-…`. */
	invoiceNumber: z.string(),
	issuedOn: z.string(),
	totalIncl: Cents,
	/** The admin's download, root-relative. */
	pdfUrl: z.string(),
});
export type CreditInvoiceSummary = z.infer<typeof CreditInvoiceSummary>;

export const RefundLine = z.object({
	orderLineId: z.string(),
	productName: z.string(),
	sku: z.string(),
	quantity: z.number().int(),
	excl: Cents,
	incl: Cents,
});

/** One refund of the order. Amounts positive: what went (or goes) back. */
export const Refund = z.object({
	id: z.string(),
	orderId: z.string(),
	status: RefundStatus,
	lines: z.array(RefundLine),
	shipping: z.boolean(),
	totalExcl: Cents,
	totalVat: Cents,
	totalIncl: Cents,
	perRate: z.array(RefundAmountsPerRate),
	restock: z.boolean(),
	reason: z.string().nullable(),
	/** Why Pay.nl refused it, or what is unclear about a pending one. */
	failure: z.string().nullable(),
	paynlRefundId: z.string().nullable(),
	employee: z.object({ id: z.string(), name: z.string() }).nullable(),
	createdAt: z.iso.datetime(),
	refundedAt: z.iso.datetime().nullable(),
	/** `null` while it has not gone through. Every refund that went through has one. */
	creditInvoice: CreditInvoiceSummary.nullable(),
});
export type Refund = z.infer<typeof Refund>;

/** An order line as the refund dialog offers it: how many can still go back. */
export const RefundableLine = z.object({
	orderLineId: z.string(),
	productName: z.string(),
	sku: z.string(),
	options: z.string(),
	quantity: z.number().int(),
	refunded: z.number().int(),
	refundable: z.number().int(),
	/** Per unit on the order's basis, as the order line shows it. */
	unitPrice: Cents,
});

/** What can still be refunded of an order, for the refund dialog. */
export const RefundableOrder = z.object({
	lines: z.array(RefundableLine),
	/** The shipping line's amount incl VAT, when it can still be refunded; `null` otherwise. */
	shippingIncl: Cents.nullable(),
	/** What was paid and is not refunded (or being refunded) yet. */
	remainingIncl: Cents,
	/** "Order annuleren" refunds everything that is left: the order is paid and not shipped. */
	cancellable: z.boolean(),
});
export type RefundableOrder = z.infer<typeof RefundableOrder>;

const OrderPath = z.object({ orderId: z.string().min(1) });

/** What to refund. */
export const RefundSelection = z.object({
	lines: z
		.array(z.object({ orderLineId: z.string().min(1), quantity: z.number().int().positive() }))
		.max(200)
		.default([]),
	/** The shipping line: always with `all`, optional in a partial refund. */
	shipping: z.boolean().default(false),
	/** Everything still refundable, shipping included; `lines` and `shipping` are then ignored. */
	all: z.boolean().default(false),
});

export const RefundPreview = z.object({
	lines: z.array(z.object({ orderLineId: z.string(), quantity: z.number().int(), excl: Cents, incl: Cents })),
	shippingIncl: Cents.nullable(),
	perRate: z.array(RefundAmountsPerRate),
	totalExcl: Cents,
	totalVat: Cents,
	totalIncl: Cents,
});
export type RefundPreview = z.infer<typeof RefundPreview>;

export const CreateRefundBody = RefundSelection.extend({
	/** Made when the dialog opens: the same request again finds the refund it made. */
	idempotencyKey: z.string().min(16).max(64),
	/** "Voorraad terugboeken": on by default; off for damaged or not returned goods. */
	restock: z.boolean().default(true),
	/** Refund everything that is left and cancel the order ("Order annuleren" on a paid order). */
	cancelOrder: z.boolean().default(false),
	reason: z.string().trim().max(500).optional(),
});
export type CreateRefundBody = z.input<typeof CreateRefundBody>;

export const ResolveRefundBody = z.object({
	/** True: Pay.nl shows it refunded, so it goes through; false: it did not, the refund fails. */
	refunded: z.boolean(),
});
export type ResolveRefundBody = z.infer<typeof ResolveRefundBody>;

export const RefundContract = {
	/**
	 * The amounts a refund of this selection gives back, computed as the refund would. Writes
	 * nothing. Errors: `RefundOrderNotFound` (404), `RefundNotAllowed` (409), `RefundTooMuch` (409).
	 */
	preview: oc
		.route({ method: "POST", path: "/admin/orders/{orderId}/refunds/preview" })
		.input(OrderPath.extend(RefundSelection.shape))
		.output(RefundPreview),
	/**
	 * Refunds through Pay.nl: then the credit invoice, the stock back (with `restock`), the order's
	 * events and payment status, and the mail with the credit invoice. The same `idempotencyKey`
	 * again answers the same refund. Errors: `RefundOrderNotFound` (404), `RefundNotAllowed` (409),
	 * `RefundTooMuch` (409), `RefundInProgress` (409: another refund of the order is pending),
	 * `RefundRejected` (502: Pay.nl refused; nothing changed), `RefundUnavailable` (503: Pay.nl
	 * was not reached; nothing changed), `RefundUncertain` (503: Pay.nl's answer was lost; check
	 * Pay.nl and resolve).
	 */
	create: oc
		.route({ method: "POST", path: "/admin/orders/{orderId}/refunds" })
		.input(OrderPath.extend(CreateRefundBody.shape))
		.output(Refund),
	/**
	 * Settles a pending refund after checking Pay.nl. Errors: `RefundNotFound` (404),
	 * `RefundNotPending` (409).
	 */
	resolve: oc
		.route({ method: "POST", path: "/admin/refunds/{refundId}/resolve" })
		.input(z.object({ refundId: z.string().min(1) }).extend(ResolveRefundBody.shape))
		.output(Refund),
};
